import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { parseQueryParamToBoolean, parseQueryParamToList } from "@/helpers/query-params";
import { useProject } from "@/hooks/Network/useProject";
import { useDebounceValue } from "@/hooks/useDebounce";
import {
  getLocalStorageValue,
  getSessionStorageValue,
  updateSessionStorage,
  useUpdateLocalStorage,
  useUpdateSessionStorage,
} from "@/hooks/useLocalStorage";
import { useQueryParams } from "@/hooks/useQueryParam";
import type { AdminTicketTabs, TicketSorting } from "@/queries/tickets/constants";
import {
  CLOSED_TICKET_TAB,
  IN_PROGRESS_TICKET_TAB,
  NEW_AND_INPROGRESS_TICKET_TAB,
  NEW_TICKET_TAB,
  REMINDER_TICKET_TAB,
  ticketRatingValues,
  ticketSortingValues,
  ticketVisibilityValues,
} from "@/queries/tickets/constants";
import { parseTicketFilterDate, ticketDateBoundary } from "@/queries/tickets/date-filters";
import { ticketStorageKeys } from "@/queries/tickets/filters";
import { ticketMutations } from "@/queries/tickets/mutations";
import type { TicketsFilters } from "@/queries/tickets/queryOptions";
import { useTicketQueries } from "@/queries/tickets/queryOptions";
import { useTranslation } from "@/translations";
import type { ApiQueryParams } from "@/types/api-types";

import type { TicketsRouteParams } from "../../routes";
import { mapTicketAssigneeIdsToApi } from "./components/TicketAssigneeFilter";
import type { TicketsFilterValues } from "./components/TicketsFilterDrawer";
import { getActiveTicketFilterCount } from "./helpers";
import { AMOUNT_TICKET_LIMIT, TicketListContent, type TicketListContentProps } from "./TicketListContent";

export type { TicketsFilters };

const TABS = [
  NEW_TICKET_TAB,
  IN_PROGRESS_TICKET_TAB,
  CLOSED_TICKET_TAB,
  REMINDER_TICKET_TAB,
  NEW_AND_INPROGRESS_TICKET_TAB,
];

type TicketsSortingParams = { [key in AdminTicketTabs]: TicketSorting };
type AllTicketsQueryParams = ApiQueryParams<"getAdminTicketsV1">;

export function TicketListPage(): React.ReactNode {
  const [queryParams, setQueryParams] = useQueryParams<TicketsRouteParams["list"]>();
  const { t } = useTranslation();
  const project = useProject();

  const TICKET_FILTERS_KEY = ticketStorageKeys.filters(project.id);
  const TICKET_TAB_SORT_KEY = ticketStorageKeys.tabSort();
  const TICKET_TAB_RESIDENT_FOCUS_KEY = ticketStorageKeys.residentFocusMode();

  const [storedResidentFocus, setStoredResidentFocus] = useState<boolean>(() =>
    getSessionStorageValue<boolean>(TICKET_TAB_RESIDENT_FOCUS_KEY, false),
  );
  const [sorting, setSorting] = useState<TicketsSortingParams>(() => {
    const storage = getLocalStorageValue<Partial<TicketsSortingParams>>(TICKET_TAB_SORT_KEY, {});
    const urlSort =
      queryParams.sort && ticketSortingValues.includes(queryParams.sort as Exclude<TicketSorting, undefined>)
        ? (queryParams.sort as TicketSorting)
        : undefined;
    const activeTabKey = (
      queryParams.tab && TABS.includes(queryParams.tab as any) ? queryParams.tab : TABS[0]
    ) as AdminTicketTabs;

    const defaults: TicketsSortingParams = {
      new: storage.new && ticketSortingValues.includes(storage.new) ? storage.new : "oldest",
      inProgress:
        storage.inProgress && ticketSortingValues.includes(storage.inProgress)
          ? storage.inProgress
          : "mostRecentActivity",
      closed: storage.closed && ticketSortingValues.includes(storage.closed) ? storage.closed : "mostRecentActivity",
      reminder: "mostRecentReminder",
      newAndInProgress:
        storage.newAndInProgress && ticketSortingValues.includes(storage.newAndInProgress)
          ? storage.newAndInProgress
          : "mostRecentActivity",
    };

    return urlSort ? { ...defaults, [activeTabKey]: urlSort } : defaults;
  });

  // Restore filter params from session storage if the URL has none
  useEffect(() => {
    const filterParamKeys: Array<keyof TicketsRouteParams["list"]> = [
      "tab",
      "addressTypes",
      "assigneeIds",
      "assigneeMode",
      "categoryIds",
      "visibility",
      "ratings",
      "statusIds",
      "closedFrom",
      "closedTo",
      "ratedFrom",
      "ratedTo",
      "isUnreadOnly",
    ];
    const hasFilterParams = filterParamKeys.some((key) => queryParams[key] != null);
    if (!hasFilterParams) {
      const stored = getSessionStorageValue<Partial<TicketsRouteParams["list"]>>(TICKET_FILTERS_KEY, {});
      if (Object.values(stored).some(Boolean)) {
        setQueryParams(stored, { replace: true });
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [search, setSearch] = useState(() => queryParams.search ?? "");
  const activeTab = (
    queryParams.tab && TABS.includes(queryParams.tab as any) ? queryParams.tab : TABS[0]
  ) as AdminTicketTabs;
  const currFilter = useMemo(() => {
    return {
      assigneeIds: parseQueryParamToList(queryParams.assigneeIds),
      assigneeMode: queryParams.assigneeMode === "exclude" ? "exclude" : "include",
      addressTypes: parseQueryParamToList(queryParams.addressTypes),
      categoryIds: parseQueryParamToList(queryParams.categoryIds),
      ratedFrom: parseTicketFilterDate(queryParams.ratedFrom),
      ratedTo: parseTicketFilterDate(queryParams.ratedTo),
      closedFrom: parseTicketFilterDate(queryParams.closedFrom),
      closedTo: parseTicketFilterDate(queryParams.closedTo),
      visibility:
        queryParams.visibility && ticketVisibilityValues.includes(queryParams.visibility as any)
          ? (queryParams.visibility as TicketsFilterValues["visibility"])
          : undefined,
      ratings: parseQueryParamToList(queryParams.ratings)
        ?.map(Number)
        .filter((n) => ticketRatingValues.includes(n as (typeof ticketRatingValues)[number])),
      statusIds: parseQueryParamToList(queryParams.statusIds),
      isUnreadOnly: parseQueryParamToBoolean(queryParams.isUnreadOnly),
    } satisfies TicketsFilterValues;
  }, [
    queryParams.addressTypes,
    queryParams.assigneeIds,
    queryParams.assigneeMode,
    queryParams.categoryIds,
    queryParams.closedFrom,
    queryParams.closedTo,
    queryParams.isUnreadOnly,
    queryParams.ratedFrom,
    queryParams.ratedTo,
    queryParams.ratings,
    queryParams.statusIds,
    queryParams.visibility,
  ]);

  useUpdateSessionStorage(TICKET_TAB_RESIDENT_FOCUS_KEY, storedResidentFocus);
  useUpdateLocalStorage(TICKET_TAB_SORT_KEY, sorting);

  const debouncedQuery = useDebounceValue(search.toLowerCase().trim());

  useEffect(() => {
    setQueryParams({ search: debouncedQuery || undefined }, { replace: true });
  }, [debouncedQuery, setQueryParams]);

  const residentFocusMode = activeTab === "inProgress" && storedResidentFocus;
  const sortBy = (residentFocusMode ? "leastRecentActivity" : sorting[activeTab] || "newest") satisfies TicketSorting;

  const handleUpdateSorting = useCallback(
    (value: TicketSorting) => {
      setSorting((oldSorting) => ({
        ...oldSorting,
        [activeTab]: value,
      }));
      setQueryParams({ sort: value }, { replace: true });
    },
    [activeTab, setQueryParams],
  );

  const handleUpdateResidentFocusMode = (value: boolean) => {
    setStoredResidentFocus(value);
  };

  const handleChangeTab = useCallback(
    (tab: AdminTicketTabs) => {
      const newQueryParams = {
        ...queryParams,
        tab,
        sort: sorting[tab],
      };

      setQueryParams(newQueryParams);
      updateSessionStorage(TICKET_FILTERS_KEY, newQueryParams);
    },
    [TICKET_FILTERS_KEY, queryParams, setQueryParams, sorting],
  );

  const handleUpdateFilter = (newFilter: TicketsFilterValues) => {
    const newQueryParams: TicketsRouteParams["list"] = {
      ...queryParams,
      addressTypes: newFilter.addressTypes?.join(","),
      assigneeIds: newFilter.assigneeIds?.join(","),
      assigneeMode: newFilter.assigneeIds?.length && newFilter.assigneeMode === "exclude" ? "exclude" : undefined,
      categoryIds: newFilter.categoryIds?.join(","),
      visibility: newFilter.visibility,
      ratings: newFilter.ratings?.join(","),
      statusIds: newFilter.statusIds?.join(","),
      closedFrom: newFilter.closedFrom,
      closedTo: newFilter.closedTo,
      ratedFrom: newFilter.ratedFrom,
      ratedTo: newFilter.ratedTo,
      isUnreadOnly: newFilter.isUnreadOnly ? "true" : undefined,
    };

    setQueryParams(newQueryParams);
    updateSessionStorage(TICKET_FILTERS_KEY, newQueryParams);
  };

  const query = useMemo(
    () => ({
      AddressTypes: currFilter.addressTypes,
      AssigneeIds: mapTicketAssigneeIdsToApi(currFilter.assigneeIds),
      AssigneeMode: currFilter.assigneeMode,
      CategoryIds: currFilter.categoryIds,
      Filter: currFilter.visibility,
      RatedFrom: ticketDateBoundary(currFilter.ratedFrom, "from"),
      RatedTo: ticketDateBoundary(currFilter.ratedTo, "to"),
      ClosedFrom: ticketDateBoundary(currFilter.closedFrom, "from"),
      ClosedTo: ticketDateBoundary(currFilter.closedTo, "to"),
      Ratings: currFilter.ratings,
      StatusIds: currFilter.statusIds,
      UnreadOnly: currFilter.isUnreadOnly,
      SortBy: sortBy,
      Tab: activeTab,
      ResidentFocusMode: residentFocusMode,
      Search: debouncedQuery,
    }),
    [
      currFilter.addressTypes,
      currFilter.assigneeIds,
      currFilter.assigneeMode,
      currFilter.categoryIds,
      currFilter.closedFrom,
      currFilter.closedTo,
      currFilter.isUnreadOnly,
      currFilter.ratedFrom,
      currFilter.ratedTo,
      currFilter.ratings,
      currFilter.statusIds,
      currFilter.visibility,
      sortBy,
      activeTab,
      residentFocusMode,
      debouncedQuery,
    ],
  ) satisfies AllTicketsQueryParams;

  const ticketQueries = useTicketQueries();
  const {
    data: ticketData,
    hasNextPage: hasMoreTickets,
    isFetching: isFetchingTickets,
    isFetchingNextPage: isLoadingMoreTickets,
    fetchNextPage: loadMoreTickets,
    error: ticketsError,
  } = useInfiniteQuery(ticketQueries.listForAdmin(query));

  // exclude tab and sortby to prevent refetching because they do not affect the amount of tickets
  const tabsStatsQuery = { ...query, Tab: undefined, SortBy: undefined, ResidentFocusMode: false as const };
  const { data: tabsStats, error: tabsStatsError } = useQuery(ticketQueries.tabsStats(tabsStatsQuery));
  const isFilterActive = getActiveTicketFilterCount(currFilter) > 0 || !!debouncedQuery || residentFocusMode;

  const { data: totalTicketsInTab, error: totalTicketsInTabError } = useQuery({
    ...ticketQueries.totalTicketsInTab(activeTab),
    enabled: isFilterActive,
  });

  const {
    data: ticketFilterOptions,
    isPending: isPendingTicketFilterOptions,
    error: ticketFilterOptionsError,
  } = useQuery(ticketQueries.filterOptions());

  const exportTicketsMutation = ticketMutations.useExportTickets();
  const deleteTicket = ticketMutations.useDeleteTicket();
  const markAllAsReadMutation = ticketMutations.useMarkAllAsRead();
  const createQuickReplyLink = ticketMutations.useGetQuickReplyLink();

  const totalTickets = ticketData?.pages[0].total;
  const tickets = useMemo(() => ticketData?.pages.flatMap((x) => x.items) ?? [], [ticketData]);

  if (ticketsError || totalTicketsInTabError || tabsStatsError || ticketFilterOptionsError) {
    return <ErrorPage error={ticketsError} />;
  }

  if (isPendingTicketFilterOptions) {
    return <FullSizeLoader withPadding />;
  }

  const contentProps: TicketListContentProps = {
    search,
    onChangeSearch: setSearch,
    tab: activeTab,
    onChangeTab: handleChangeTab,
    residentFocusMode,
    onUpdateResidentFocusMode: handleUpdateResidentFocusMode,
    sorting: sortBy,
    onUpdateSorting: handleUpdateSorting,
    filter: currFilter,
    onUpdateFilter: handleUpdateFilter,
    totalTickets,
    totalTicketsInTab: isFilterActive ? totalTicketsInTab?.tickets : totalTickets,
    tabsStats,
    tickets,
    isLoadingTickets: isFetchingTickets,
    hasMoreTickets,
    loadMoreTickets,
    isLoadingMoreTickets,
    filterOptions: ticketFilterOptions,
    exportTickets: () =>
      exportTicketsMutation.mutateAsync({
        query: { ...query, Limit: AMOUNT_TICKET_LIMIT, Offset: 0, Tab: undefined },
        filename: getTicketExportFilename(t("page.tickets.export.filename"), project.name),
      }),
    isDownloading: exportTicketsMutation.isPending,
    deleteTicket: deleteTicket.mutateAsync,
    markAsRead: () => markAllAsReadMutation.mutateAsync({ ...query }),
    copyQuickReplyLink: createQuickReplyLink.mutate,
  };

  return <TicketListContent {...contentProps} />;
}

function getTicketExportFilename(filenameTranslation: string, projectName: string): string {
  const cleanProjectName = projectName.replaceAll(/[^a-zA-Z0-9()| _-]+/g, "");
  const date = new Date()
    .toISOString()
    .substring(0, 16)
    .replaceAll(/[T\-:]/g, "");

  return `${filenameTranslation}_${date}_${cleanProjectName}.xlsx`;
}
